import { deviceTimeZone, localDateString } from '@gramas/shared'
import { cn, Spinner, useBackLayer } from '@gramas/ui'
import type { ReactNode } from 'react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { CeremonyHost } from '../ceremony/CeremonyHost.js'
import { DoorBar } from '../glitime/DoorBar.js'
import { FirstMeeting } from '../glitime/FirstMeeting.js'
import {
  endFirstMeeting,
  glitimeMet,
  startFirstMeeting,
  useFirstMeetingState,
} from '../glitime/store.js'
import { closeZone, currentZonePhase, openDoor, useZoneState } from '../glitime/zone.js'
import { PresenceController } from '../presence/PresenceController.js'
import { currentStreak } from '../sass/facts.js'
import { announceAwards } from '../sass/toast.js'
import { Foods } from '../screens/Foods.js'
import { Settings } from '../screens/Settings.js'
import { BodySheetHost } from '../screens/sheets/BodySheet.js'
import { openBodySheet } from '../screens/sheets/bodySheetStore.js'
import { Today } from '../screens/Today.js'
import { Trends } from '../screens/Trends.js'
import { currentProfile } from '../screens/useProfile.js'
import { startSyncTriggers } from '../sync/engine.js'
import { endTour, startTour, tourDismissed, useTourState } from '../tour/store.js'
import { Tour } from '../tour/Tour.js'
import { checkAwards } from '../trophies/detect.js'
import { openWrapped, wrappedOnOffer } from '../wrapped/store.js'
import { currentWeekStart, WrappedHost } from '../wrapped/Wrapped.js'
import { InstallBanner } from './InstallBanner.js'
import { SyncBanner } from './SyncBanner.js'
import { TabBar, type TabKey } from './TabBar.js'
import { Toasts } from './Toasts.js'

// The scanner drags in the barcode and OCR engines; keeping it out of the entry
// bundle is what lets the diary open immediately on a cold start.
const Scan = lazy(() => import('../screens/Scan.js').then((m) => ({ default: m.Scan })))

/**
 * Panes hide with `visibility`, not unmounting or `display: none`: unmounting
 * loses scroll and search state, and engines are allowed to drop a scroller's
 * offset once it stops being laid out. The hidden panes' live queries keep
 * running, which at two users' worth of data costs nothing.
 */
function Pane({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <div
      data-pane
      data-active={active || undefined}
      className={cn('absolute inset-0', !active && 'invisible')}
    >
      {children}
    </div>
  )
}

/**
 * One history entry for one visited tab. Rendering these from the trail rather
 * than pushing by hand is what keeps the two in step: React mounts one when a
 * tab is entered and unmounts one when the trail is cut, whatever cut it.
 */
function TrailEntry({ onBack }: { onBack: () => void }) {
  useBackLayer(true, onBack)
  return null
}

/** Visiting the same tab twice makes two stops, so identity cannot be the tab. */
interface Stop {
  id: number
  tab: TabKey
}

let nextStopId = 1

/** The diary is stop zero and never leaves; Back past it leaves the app. */
const HOME: Stop = { id: 0, tab: 'today' }

/**
 * The app is a fixed viewport with one scrolling pane, not a scrolling
 * document: a page that rubber-bands under a fixed tab bar reads as broken on
 * iOS. The shell owns the notch inset so banners and every tab header sit
 * below the status bar without padding themselves.
 */
export function Shell({
  onSignedOut,
  bootPending = false,
}: {
  onSignedOut: () => void
  /** The launch animation is still on top; nothing may open under it. */
  bootPending?: boolean
}) {
  /**
   * Where you have been, not just where you are. Back walks this down one tab
   * at a time and only leaves the app once the diary is all that is left —
   * which is also why the diary is the one tab that truncates it instead of
   * extending it: it is home, not another stop on the way.
   */
  // Launcher shortcuts land on /?tab=scan; nothing else is addressable.
  const [trail, setTrail] = useState<Stop[]>(() =>
    new URLSearchParams(location.search).get('tab') === 'scan'
      ? [HOME, { id: nextStopId++, tab: 'scan' }]
      : [HOME],
  )
  /**
   * The tab the tour is pointing at, which is not a stop on any journey: it
   * leaves no trail entry and no history entry, so Back keeps belonging to the
   * tour for as long as it is running and the app is exactly where it was left
   * once it ends.
   */
  const [tourTab, setTourTab] = useState<TabKey | null>(null)
  const tourOpen = useTourState((state) => state.open)
  const tourRun = useTourState((state) => state.run)
  const meetingOpen = useFirstMeetingState((state) => state.open)
  const tab = (tourOpen ? tourTab : null) ?? trail[trail.length - 1]?.tab ?? 'today'
  // Reactive, unlike `currentZonePhase()` below: this one decides what
  // renders in the tab bar's own spot, so it has to be a subscription.
  const zoneOpen = useZoneState((state) => state.phase !== 'closed')

  const goTo = (next: TabKey) => {
    // Switching tabs while she is up is a cut, not a goodbye: closing the
    // zone gracefully (`beginCloseZone`) would animate for half a second
    // while navigation is also happening, fighting the tab change for the
    // same span of time.
    if (currentZonePhase() !== 'closed') closeZone()
    setTrail((current) => {
      if (next === current[current.length - 1]?.tab) return current
      return next === 'today' ? [HOME] : [...current, { id: nextStopId++, tab: next }]
    })
  }

  useEffect(() => startSyncTriggers(), [])

  /**
   * Trophies once per launch, as well as after every log.
   *
   * Two of them — the streak beats and the account's own anniversary — are
   * earned by a day passing rather than by anything being written, so an
   * account that logged nothing today would otherwise learn about its
   * hundredth straight day tomorrow. Deliberately after the splash, for the
   * same reason the tour is: nothing should toast over the boot animation.
   */
  useEffect(() => {
    if (bootPending) return
    const today = localDateString(new Date(), deviceTimeZone())
    void currentStreak(today)
      .then((streak) => checkAwards(streak, currentProfile()?.createdAt ?? null))
      .then(announceAwards)
  }, [bootPending])

  /**
   * The Sunday push deep-links here. The service worker already routes a
   * payload URL to the app; `?wrapped=1` is the app's half of that contract.
   * The parameter is stripped once acted on so a reload does not reopen a
   * report that was closed on purpose.
   */
  useEffect(() => {
    if (bootPending) return
    const url = new URL(window.location.href)
    if (url.searchParams.get('wrapped') == null) return
    url.searchParams.delete('wrapped')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    // A push sent on Sunday evening and opened on Monday morning is still
    // about Sunday's week, which is exactly what the offer window answers.
    openWrapped(wrappedOnOffer(new Date()) ?? currentWeekStart())
  }, [bootPending])

  /**
   * Her scale push deep-links here the same way: `?body=1` opens the body
   * panel over whatever tab the app comes up on. Stripped once acted on for
   * the same reason as above.
   */
  useEffect(() => {
    if (bootPending) return
    const url = new URL(window.location.href)
    if (url.searchParams.get('body') == null) return
    url.searchParams.delete('body')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    openBodySheet()
  }, [bootPending])

  /**
   * Offer the tour once per launch, unless it has been sent away for good —
   * but only once this account has met Glitimė first. She is not a step in
   * the feature walkthrough; the walkthrough is one of the things she narrates.
   *
   * Asked after the splash rather than beside it: the sequence ends by landing
   * the mark on the day's ring, and a dark screen dropping over that is the one
   * moment in the app where two animations would be fighting for the same
   * second. The guard is a ref because the answer arrives asynchronously and
   * the effect re-runs when the splash finishes.
   */
  const offerTour = () => {
    void tourDismissed().then((dismissed) => {
      if (!dismissed) startTour()
    })
  }
  // Read through a ref, the same way the tour itself reads `onShowTab`: the
  // effect below must not re-run every render just because this closure is a
  // new one, and `offerTour` has no reactive dependencies of its own.
  const offerTourRef = useRef(offerTour)
  offerTourRef.current = offerTour

  const offered = useRef(false)
  useEffect(() => {
    if (bootPending || offered.current) return
    offered.current = true
    let cancelled = false
    void glitimeMet().then((met) => {
      if (cancelled) return
      if (!met) {
        startFirstMeeting()
        return
      }
      offerTourRef.current()
    })
    return () => {
      cancelled = true
    }
  }, [bootPending])

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-bg',
        // Scan is full-bleed under the notch and paints its own insets.
        tab !== 'scan' && 'pt-[env(safe-area-inset-top)]',
      )}
    >
      {trail.slice(1).map((stop) => (
        <TrailEntry key={stop.id} onBack={() => setTrail((current) => current.slice(0, -1))} />
      ))}
      {/* Headless. Works out who she is today and publishes it — to the
          greeting, to the toast layer, and to the root element, where her
          palette and her breathing rate are selected in CSS. */}
      <PresenceController />
      <SyncBanner />
      <InstallBanner />
      <main className="relative min-h-0 flex-1 overflow-hidden">
        <Pane active={tab === 'today'}>
          <Today />
        </Pane>
        {tab === 'scan' && (
          <div className="absolute inset-0">
            <Suspense
              fallback={
                <div className="grid h-full place-items-center bg-black">
                  <Spinner className="size-6 text-white/70" />
                </div>
              }
            >
              <Scan onDone={() => goTo('today')} />
            </Suspense>
          </div>
        )}
        <Pane active={tab === 'foods'}>
          <Foods />
        </Pane>
        <Pane active={tab === 'trends'}>
          <Trends />
        </Pane>
        <Pane active={tab === 'settings'}>
          <Settings onSignedOut={onSignedOut} />
        </Pane>
        <Toasts />
      </main>
      {zoneOpen ? (
        <DoorBar
          onByla={() => openDoor('dossier')}
          onPazadai={() => openDoor('promises')}
          onTrofejai={() => openDoor('trophies')}
        />
      ) : (
        <TabBar value={tab} onChange={goTo} />
      )}
      <WrappedHost />
      <BodySheetHost />
      <CeremonyHost ready={!bootPending} />
      {tourOpen && (
        <Tour
          // A replay starts from the first step rather than resuming, so the
          // whole thing is remounted rather than merely re-shown.
          key={tourRun}
          tab={tab}
          onShowTab={setTourTab}
          onDone={() => {
            endTour()
            setTourTab(null)
          }}
        />
      )}
      {meetingOpen && (
        <FirstMeeting
          onDone={() => {
            endFirstMeeting()
            offerTour()
          }}
        />
      )}
    </div>
  )
}
